import React, { useState } from 'react';
import { Dialog, Slide, AppBar, Toolbar, IconButton, Typography, Box, Button, LinearProgress, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QuestionCard from './QuestionCard';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function QuizDialog({ open, onClose, title, questions, onSubmit, loading }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [reviewing, setReviewing] = useState(false);

  const total = questions?.length || 0;
  const current = questions?.[currentIdx];
  const progress = total > 0 ? ((currentIdx + 1) / total) * 100 : 0;
  const allAnswered = total > 0 && Object.keys(answers).length === total;

  const handleSelect = (idx) => {
    setAnswers((prev) => ({ ...prev, [current.question_id]: idx }));
  };

  const handleNext = () => { if (currentIdx < total - 1) setCurrentIdx((i) => i + 1); };
  const handlePrev = () => { if (currentIdx > 0) setCurrentIdx((i) => i - 1); };

  const handleSubmit = async () => {
    const formatted = questions.map((q) => ({
      question_id: q.question_id,
      selected_index: answers[q.question_id],
    }));
    const res = await onSubmit(formatted);
    setResults(res);
    setReviewing(true);
    setCurrentIdx(0);
  };

  const handleClose = () => {
    setCurrentIdx(0);
    setAnswers({});
    setResults(null);
    setReviewing(false);
    onClose(results);
  };

  const currentResult = reviewing
    ? results?.results?.find((r) => r.question_id === current?.question_id)
    : null;

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative', background: '#0d1a2e', boxShadow: 'none', borderBottom: '1px solid rgba(78,219,161,0.1)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}><CloseIcon /></IconButton>
          <Typography sx={{ ml: 2, flex: 1, fontFamily: '"Fraunces", serif', fontWeight: 500 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {currentIdx + 1} / {total}
          </Typography>
        </Toolbar>
        <LinearProgress variant="determinate" value={progress}
          sx={{ height: 3, background: 'rgba(78,219,161,0.1)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #4edba1, #2db87a)' } }} />
      </AppBar>

      <Box sx={{ maxWidth: 640, mx: 'auto', width: '100%', px: 3, py: 4 }}>
        {loading && <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 8 }}>Loading questions...</Typography>}

        {!loading && !reviewing && current && (
          <>
            <QuestionCard question={current} selectedIndex={answers[current.question_id]} onSelect={handleSelect} showResult={false} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={handlePrev} disabled={currentIdx === 0}
                sx={{ borderColor: 'rgba(78,219,161,0.2)', color: 'text.secondary', '&:hover': { borderColor: '#4edba1' } }}>
                Back
              </Button>
              {currentIdx < total - 1 ? (
                <Button variant="contained" onClick={handleNext} disabled={answers[current.question_id] === undefined}>
                  Next
                </Button>
              ) : (
                <Button variant="contained" onClick={handleSubmit} disabled={!allAnswered}
                  sx={{ background: allAnswered ? 'linear-gradient(135deg, #f0a856, #d4893a)' : undefined, boxShadow: allAnswered ? '0 4px 20px rgba(240,168,86,0.3)' : undefined }}>
                  Submit All
                </Button>
              )}
            </Box>
          </>
        )}

        {reviewing && current && (
          <>
            {currentIdx === 0 && results && (
              <Paper sx={{ mb: 4, p: 3, textAlign: 'center', background: 'rgba(78,219,161,0.06)', borderColor: 'rgba(78,219,161,0.15)' }}>
                <Typography variant="h4" sx={{ color: 'primary.main' }}>
                  {results.score} / {results.total}
                </Typography>
                <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {results.score / results.total >= 0.7 ? 'Great work!' : results.score / results.total >= 0.4 ? 'Good effort — review the rationales below.' : "Keep studying — you'll get there."}
                </Typography>
              </Paper>
            )}

            <QuestionCard question={current} selectedIndex={answers[current.question_id]} onSelect={() => {}} showResult={true} result={currentResult} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={handlePrev} disabled={currentIdx === 0}
                sx={{ borderColor: 'rgba(78,219,161,0.2)', color: 'text.secondary' }}>
                Back
              </Button>
              {currentIdx < total - 1 ? (
                <Button variant="contained" onClick={handleNext}>Next</Button>
              ) : (
                <Button variant="contained" onClick={handleClose}
                  sx={{ background: 'linear-gradient(135deg, #4edba1, #2db87a)' }}>
                  Done
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}
